/**
 * Утилиты для работы с иерархией организаций
 * 
 * Организации теперь хранятся в KLS (qual_id = 336).
 * Иерархия строится на основе рубрики (kls_rubrika) - ltree пути вида "1.2.3"
 */

import type { Organization } from "@/types/api-v2";

export interface OrganizationNode extends Organization {
  children: OrganizationNode[];
  level: number;
}

/**
 * Строит иерархическое дерево из плоского списка организаций на основе рубрики
 */
export function buildOrganizationsHierarchy(organizations: Organization[]): OrganizationNode[] {
  if (!organizations || organizations.length === 0) {
    console.log('[buildOrganizationsHierarchy] No organizations provided');
    return [];
  }

  console.log('[buildOrganizationsHierarchy] Processing', organizations.length, 'items');

  // Создаем словарь для быстрого доступа по рубрике и ID
  const nodesByRubrika = new Map<string, OrganizationNode>();
  const nodesById = new Map<number, OrganizationNode>();
  
  // Сначала создаем все узлы
  organizations.forEach((org) => {
    const rubrika = org.rubrika || '';
    const node: OrganizationNode = {
      ...org,
      children: [],
      level: 0,
    };
    
    if (rubrika) {
      nodesByRubrika.set(rubrika, node);
    }
    nodesById.set(org.id, node);
  });
  
  console.log('[buildOrganizationsHierarchy] nodesByRubrika size:', nodesByRubrika.size);
  
  // Строим дерево на основе рубрики
  const rootNodes: OrganizationNode[] = [];
  let childCount = 0;
  
  organizations.forEach((org) => {
    const node = nodesById.get(org.id)!;
    const rubrika = org.rubrika || '';
    
    if (!rubrika) {
      // Нет рубрики - корневой узел
      rootNodes.push(node);
      return;
    }
    
    // Определяем уровень вложенности по количеству точек в рубрике
    const parts = rubrika.split('.').filter(p => p.length > 0);
    
    if (parts.length === 0) {
      // Пустая рубрика - корневой узел
      rootNodes.push(node);
      return;
    }
    
    node.level = parts.length - 1;
    
    if (parts.length > 1) {
      // Есть родитель - находим его по родительской рубрике
      const parentRubrika = parts.slice(0, -1).join('.');
      const parent = nodesByRubrika.get(parentRubrika);
      
      if (parent) {
        parent.children.push(node);
        childCount++;
      } else {
        // Родитель не найден - добавляем в корень
        console.warn(`[buildOrganizationsHierarchy] Parent not found for rubrika: ${rubrika}, expected parent: ${parentRubrika}`);
        rootNodes.push(node);
      }
    } else {
      // Корневой узел (рубрика без точек или один сегмент)
      rootNodes.push(node);
    }
  });
  
  console.log('[buildOrganizationsHierarchy] Result: rootNodes:', rootNodes.length, 'children attached:', childCount);
  
  // Сортируем узлы по рубрике (это сохранит правильный порядок иерархии)
  const sortNodes = (nodes: OrganizationNode[]) => {
    nodes.sort((a, b) => {
      const rubrikaA = a.rubrika || '';
      const rubrikaB = b.rubrika || '';
      
      if (!rubrikaA && !rubrikaB) return 0;
      if (!rubrikaA) return 1;
      if (!rubrikaB) return -1;
      
      // Сравниваем по числовым значениям в рубрике
      return rubrikaA.localeCompare(rubrikaB, undefined, { numeric: true, sensitivity: 'base' });
    });
    
    nodes.forEach((node) => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };
  
  sortNodes(rootNodes);
  
  return rootNodes;
}

/**
 * Преобразует иерархическое дерево в плоский список для использования в select
 * Сохраняет уровень вложенности для визуального отображения через CSS
 * 
 * displayName использует name_short (краткое название) если доступно,
 * иначе полное name. При наведении показывается полное название (title).
 */
export function flattenOrganizationsHierarchy(
  nodes: OrganizationNode[],
  level: number = 0
): Array<OrganizationNode & { displayName: string; indentLevel: number }> {
  const result: Array<OrganizationNode & { displayName: string; indentLevel: number }> = [];
  
  nodes.forEach((node) => {
    result.push({
      ...node,
      // Используем краткое название если есть, иначе полное
      displayName: node.name_short || node.name,
      indentLevel: level,
    });
    
    if (node.children.length > 0) {
      result.push(...flattenOrganizationsHierarchy(node.children, level + 1));
    }
  });
  
  console.log('[flattenOrganizationsHierarchy] Result:', result.length, 'items');
  
  return result;
}

/**
 * Получает корневые организации (первый уровень иерархии)
 */
export function getRootOrganizations(organizations: Organization[]): Organization[] {
  return organizations.filter(org => {
    if (!org.rubrika) return true;
    return !org.rubrika.includes('.');
  });
}

/**
 * Получает дочерние организации для указанного родителя
 */
export function getChildOrganizations(
  organizations: Organization[], 
  parentRubrika: string
): Organization[] {
  return organizations.filter(org => {
    if (!org.rubrika) return false;
    const parts = org.rubrika.split('.');
    if (parts.length <= 1) return false;
    const parentPath = parts.slice(0, -1).join('.');
    return parentPath === parentRubrika;
  });
}
